/**
 * aiChatHistoryFolders · 历史 folder 类消息懒加载文件夹树
 *
 * 协议对齐 (接口 5):
 *   POST /web/workgroup/folder/ai/chat/history/message/folders
 *   body: { group_id, message_id }
 *   resp: {
 *     message_id,
 *     folders: PreviewFolder[],        // 当时冻结的 chain_snapshot 全嵌套树
 *     excluded_folder_keys: string[]   // 用户反选的 folder.key; 已建任务时还原反选态 (可为 [])
 *     task_created: boolean            // 已确认创建 → 预览只读
 *   }
 *
 * 调用时机:
 *   · history 接口返回的 data.type='folder' 类 message 需要展示文件夹树时, 用该
 *     message 的 message_id 调本接口拿完整 chain_snapshot 树 + 排除项
 *   · 一次拉全 (含子层全嵌套), 不用再做"展开懒加载"二次请求
 *
 * 与实时流 SSE final_proposal 的区别:
 *   · 实时流: folders 扁平结构 (name 用 "/" 分隔层级), 走 buildFolderForest 拼树
 *   · 历史快照: folders 已经是嵌套树, 直接递归映射成前端 PlanFolder
 */

import type { PlanFolder, PlanFolderRule } from '../../../types/ai-classify'

declare const __API_SERVER_HOST__: string

/**
 * 接口 5 单个 folder 节点 (PreviewFolder 结构)
 *
 * 字段是后端按维度类型动态填的, rule 形态多样 (时间 / 部门 / 多维度组合),
 * 跟前端 PlanFolderRule 的固定 schema 不完全等价 — 映射时按字段缺失默认值兜底。
 */
export interface PreviewFolder {
  /** folder 唯一 key · 跟 excluded_folder_keys 比对用 */
  key: string
  name: string
  /** 在 chain 里的维度层级 (1 起, 顶层=1) */
  level: number
  /** 业务规则 · 多维度组合时多 key 合并; 阶段 1 直接当 PlanFolderRule 子集映射 */
  rule?: Partial<PlanFolderRule> & Record<string, unknown>
  has_children?: boolean
  child_count?: number
  /** 递归子节点 (结构同本表); 无子节点时字段不出现 */
  children?: PreviewFolder[]
}

interface HistoryMessageFoldersRequest {
  group_id: string
  message_id: number
}

interface HistoryMessageFoldersResponse {
  message_id: number
  folders: PreviewFolder[]
  /** 用户反选的 folder.key · 已建任务时用于还原勾选 (恒为数组, 可为 []) */
  excluded_folder_keys?: string[]
  /** true = 用户已确认创建, 本预览只读 */
  task_created?: boolean
}

export interface FetchHistoryFoldersOptions {
  signal?: AbortSignal
}

/**
 * 调一次接口 5 · 拉指定 folder 类 message 的完整文件夹树 + 排除项
 *
 * 失败兜底: 调用方决定是 swallow 还是冒泡。本函数只负责协议层透传,
 * 不做任何上层 dispatch / state 更新 (由 conversationRestore 编排)。
 */
export async function fetchAiChatHistoryFolders(
  messageId: number,
  groupID: string,
  options: FetchHistoryFoldersOptions = {}
): Promise<HistoryMessageFoldersResponse> {
  const url =
    __API_SERVER_HOST__ +
    '/web/workgroup/folder/ai/chat/history/message/folders'
  const body: HistoryMessageFoldersRequest = {
    group_id: groupID,
    message_id: messageId
  }
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal: options.signal
  })
  if (!res.ok) {
    throw new Error(`HTTP ${res.status} ${res.statusText}`)
  }
  const json = await res.json()
  return json && json.data !== undefined ? json.data : json
}

// ============================================================
// PreviewFolder → PlanFolder 转换
// ============================================================

/**
 * 把接口 5 的嵌套 PreviewFolder 树转换为前端 PlanFolder 树
 *
 * 设计要点:
 *   · id 用层级路径拼接 (hist-folder-{key1}/{key2}/...) 保证 react key 稳定
 *     且无歧义 (跨层级 key 重名也不冲突)
 *   · parentFolderID 链接到父节点的 id; 顶层为 0
 *   · estimatedCount / source / samples 历史快照下没有意义, 填默认值 0
 *   · rule 字段从后端透传 + 默认值兜底 (PlanFolderRule 6 字段固定 schema)
 *
 * 仅做数据形态对齐, 不含历史灰显逻辑 (由 UI 层 usePlanPreview 推断 readonly 态)。
 */
export function previewFoldersToPlanFolders(
  previews: PreviewFolder[]
): PlanFolder[] {
  return mapForestWithParent(previews, 0, 'hist-folder')
}

function mapForestWithParent(
  nodes: PreviewFolder[],
  parentFolderID: 0 | string,
  idPrefix: string
): PlanFolder[] {
  return nodes.map((node) => {
    const id = `${idPrefix}-${node.key}`
    const children =
      Array.isArray(node.children) && node.children.length > 0
        ? mapForestWithParent(node.children, id, id)
        : undefined
    return {
      id,
      folderName: node.name || '',
      parentFolderID,
      source: 0,
      rule: normalizePlanFolderRule(node.rule),
      estimatedCount: 0,
      chainKey: node.key,
      ...(children ? { children } : {})
    }
  })
}

/**
 * 把后端 rule 透传成 PlanFolderRule (前端固定 6 字段 schema)
 *
 * 后端单条 rule 形态多样:
 *   · 时间: { times: [{ start, end }] }
 *   · 部门: { departmentID: ["36"] }
 *   · 多维度组合: 多 key 同时存在
 * 这里只兜底 6 字段, 其他不识别的字段不丢但也不映射 (历史快照只展示, 不参与建夹)。
 */
function normalizePlanFolderRule(
  raw: PreviewFolder['rule'] | undefined
): PlanFolderRule {
  if (!raw || typeof raw !== 'object') {
    return {
      keywords: [],
      times: [],
      departmentUserID: [],
      departmentID: [],
      nicknames: [],
      keywordAndOr: 0
    }
  }
  return {
    keywords: pickStringArray(raw, 'keywords'),
    times: Array.isArray((raw as any).times) ? ((raw as any).times as []) : [],
    departmentUserID: pickStringArray(raw, 'departmentUserID'),
    departmentID: pickStringArray(raw, 'departmentID'),
    nicknames: pickStringArray(raw, 'nicknames'),
    keywordAndOr: (raw as any).keywordAndOr === 1 ? 1 : 0
  }
}

function pickStringArray(obj: Record<string, unknown>, key: string): string[] {
  const v = obj[key]
  return Array.isArray(v) ? (v as string[]) : []
}
